import { useState } from 'react';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import { useRefreshToken } from '../common/hooks/useRefreshToken';
import ViewPage from '../layouts/ViewPage';
import { getTranslation } from '../common/utils/translate';
import { Tab, Tabs } from '@mui/material';
import BrandSettingsForm from '../components/BrandSettingsForm';
import BrandMediaForm from '../components/BrandMediaForm';
import BrandSeoForm from '../components/BrandSeoForm';
import { languages } from '../utils/translate';
import LabelAndDisplay from '../components/LabelAndDisplay';
import ImageContainer from '../components/shared/ImageContainer';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function SettingsPage() {
  const retailer = useSelector(state => state.retailer);
  const [language, setLanguage] = useState(languages[0]);

  useRefreshToken();

  const translatedRetailer = getTranslation(retailer, { key: 'languages_code', code: language.code });
  const logo = retailer.logo ? `${process.env.REACT_APP_API_URL}/assets/${retailer.logo}` : '';
  const placeholder = retailer.default_image ? `${process.env.REACT_APP_API_URL}/assets/${retailer.default_image}` : '';

  const fields = [
    {
      key: 'Name',
      value: translatedRetailer.retailer_name,
    },
    {
      key: 'SEO Title',
      value: translatedRetailer.title,
    },
    {
      key: 'SEO Description',
      value: translatedRetailer.description,
    },
    {
      key: 'Smart Video Brand',
      value: retailer?.smart_videos_brand?.name,
    },
  ];

  const sidebarFields = [
    {
      key: 'Logo',
      children: (
        <ImageContainer
          url={logo}
          alt={translatedRetailer.retailer_name}
          sx={{
            height: '20rem',
          }}
        />
      )
    },
    ...(retailer.default_image ? [
      {
        key: 'Placeholder Image',
        children: (
          <ImageContainer
            url={placeholder}
            alt={translatedRetailer.retailer_name}
            sx={{
              height: '20rem',
            }}
          />
        )
      }
    ] : []),
  ];

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <ViewPage
      title={`Profile & Settings`}
      description={`Manage your profile and settings`}
      previewUrl={`https://${retailer.subdomain}`}
      breadcrumbsLinks={[
        {
          url: '/',
          content: 'Dashboard'
        },
        {
          url: '/',
          content: 'Profile & Settings'
        }
      ]}
      language={language}
      setLanguage={setLanguage}
      prefix='View in '
    >
      <Box 
        sx={{
          flexGrow: 1,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="SEO" {...a11yProps(1)} />
            <Tab label="Brand" {...a11yProps(2)} />
            <Tab label="Media" {...a11yProps(3)} />
          </Tabs>
        </Box>
      
        <Box>
          <CustomTabPanel value={value} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7} lg={8}>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {
                        fields.map(field => (
                          <LabelAndDisplay
                            key={field.key}
                            label={field.key}
                            value={field.value}
                          >
                            {field.children}
                          </LabelAndDisplay>
                        ))
                      }
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Box sx={{ flexGrow: 1, my: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {
                        sidebarFields.map(field => (
                          <LabelAndDisplay
                            direction='column'
                            key={field.key}
                            label={field.key}
                            value={field.value}
                          >
                            {field.children}
                          </LabelAndDisplay>
                        ))
                      }
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <BrandSeoForm
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <BrandSettingsForm
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <BrandMediaForm
            />
          </CustomTabPanel>
        </Box>
      </Box>
    </ViewPage>
  )
}

export default SettingsPage